<script setup>
import { ref, watch, computed, onMounted, onBeforeUpdate, nextTick } from 'vue';
import { useStore } from 'vuex';
import { useAlert } from 'dashboard/composables';
import { useI18n } from 'vue-i18n';

import draggable from 'vuedraggable';
import TaskGroupItemRow from './TaskGroupItemRow.vue';
import TaskGroupItemDetail from './TaskGroupItemDetail.vue';

const props = defineProps({
  group: { type: Object, default: null },
});

const emit = defineEmits(['saved']);

const store = useStore();
const { t } = useI18n();
let rowRefs = [];

const name = ref('');
const items = ref([]);
let saveTimer = null;
const createdGroupId = ref(null);
let initTime = 0;
// evita criar dois grupos duplicados quando o usuário edita vários campos
// rapidamente antes da primeira criação (assíncrona) retornar e definir
// createdGroupId — enquanto uma criação está em voo, a próxima chamada de
// save() aguarda essa promise e então segue como update.
let isCreating = false;
let creationPromise = Promise.resolve();

const detailItemIndex = ref(null);
const detailItem = computed(() =>
  detailItemIndex.value !== null
    ? (items.value[detailItemIndex.value] ?? null)
    : null
);

function openDetail(idx) {
  detailItemIndex.value = idx;
}
function closeDetail() {
  detailItemIndex.value = null;
}
function onDetailUpdate(updated) {
  if (detailItemIndex.value === null) return;
  items.value.splice(detailItemIndex.value, 1, updated);
}
function newItem() {
  return {
    id: null,
    title: '',
    task_type: '',
    assignee_id: null,
    priority: 'no_priority',
    due_value: 1,
    due_unit: 'days',
    label_list: [],
    checklist_items: [],
  };
}

function addItem() {
  items.value.push(newItem());
}

function addItemAndFocus() {
  items.value.push(newItem());
  nextTick(() => {
    const last = rowRefs[items.value.length - 1];
    last?.focusTitle();
  });
}

function onRowTitleEnter(idx) {
  if (idx === items.value.length - 1) addItemAndFocus();
  else nextTick(() => rowRefs[idx + 1]?.focusTitle());
}

function updateItem(index, updated) {
  items.value.splice(index, 1, updated);
}
function removeItem(index) {
  items.value.splice(index, 1);
}

async function save() {
  if (!name.value.trim()) return;
  const payload = {
    name: name.value.trim(),
    items: items.value
      .filter(i => (i.title ?? '').trim())
      .map(i => ({
        id: i.id ?? null,
        title: i.title.trim(),
        task_type: i.task_type ?? '',
        assignee_id: i.assignee_id ?? null,
        priority: i.priority ?? 'no_priority',
        due_value: i.due_value ?? 1,
        due_unit: i.due_unit ?? 'days',
        label_list: i.label_list ?? [],
        checklist_items: i.checklist_items ?? [],
        recurrence_type: i.recurrence_type ?? null,
        recurrence_interval: i.recurrence_interval ?? null,
        recurrence_unit: i.recurrence_unit ?? null,
        recurrence_ends_at: i.recurrence_ends_at ?? null,
        recurrence_count: i.recurrence_count ?? null,
      })),
  };
  const existingId = props.group?.id ?? createdGroupId.value;
  try {
    if (existingId) {
      await store.dispatch('kanbanTaskGroups/update', {
        id: existingId,
        ...payload,
      });
    } else if (isCreating) {
      // já existe uma criação em voo: não dispara uma segunda (duplicaria o
      // grupo) — aguarda ela terminar e então salva como update com os
      // dados mais recentes do formulário.
      await creationPromise;
      // se a criação falhou, createdGroupId continua nulo e recursar aqui
      // dispararia uma nova tentativa de criação por chamada empilhada —
      // podendo duplicar o grupo caso a API tenha criado e a resposta se perdido.
      if (!createdGroupId.value) return;
      await save();
    } else {
      isCreating = true;
      let resolveCreation;
      creationPromise = new Promise(resolve => {
        resolveCreation = resolve;
      });
      try {
        const created = await store.dispatch(
          'kanbanTaskGroups/create',
          payload
        );
        createdGroupId.value = created.id;
        emit('saved');
      } finally {
        isCreating = false;
        resolveCreation();
      }
    }
  } catch {
    useAlert(t('KANBAN.TASK_GROUPS.SAVE_ERROR'));
  }
}

function scheduleSave() {
  clearTimeout(saveTimer);
  saveTimer = setTimeout(save, 800);
}

function onDetailRemove() {
  if (detailItemIndex.value === null) return;
  items.value.splice(detailItemIndex.value, 1);
  detailItemIndex.value = null;
  scheduleSave();
}

function loadChecklistCache() {
  try {
    return JSON.parse(localStorage.getItem('kanban_checklist_items') || '{}');
  } catch {
    return {};
  }
}

function getChecklistItems(i, existing, clCache) {
  if (i.checklist_items?.length) return i.checklist_items;
  if (existing?.checklist_items?.length) return existing.checklist_items;
  return clCache[i.id] ?? [];
}

function initForm() {
  clearTimeout(saveTimer);
  createdGroupId.value = null;
  initTime = Date.now();
  if (props.group) {
    const clCache = loadChecklistCache();
    name.value = props.group.name ?? '';
    items.value = (props.group.items ?? []).map(i => {
      const existing = items.value.find(local => local.id === i.id);
      return {
        ...i,
        label_list: i.label_list ?? [],
        checklist_items: getChecklistItems(i, existing, clCache),
      };
    });
  } else {
    name.value = '';
    items.value = [newItem()];
  }
}

onBeforeUpdate(() => {
  rowRefs = [];
});

onMounted(() => {
  initForm();
});

defineExpose({ name, addItem });

watch(
  () => props.group?.id,
  () => {
    initForm();
  }
);

watch(
  [name, items],
  () => {
    if (Date.now() - initTime < 200) return;
    if (name.value.trim()) scheduleSave();
  },
  { deep: true }
);
</script>

<template>
  <div class="flex flex-col gap-4">
    <div class="flex gap-4 min-h-0">
      <div class="flex flex-col gap-3 flex-1 min-w-0">
        <div
          v-if="items.length"
          class="flex flex-col border border-n-weak rounded-xl overflow-visible"
        >
          <draggable
            v-model="items"
            item-key="id"
            ghost-class="opacity-40"
            class="flex flex-col"
            @end="scheduleSave"
          >
            <template #item="{ element: item, index: idx }">
              <TaskGroupItemRow
                :ref="
                  el => {
                    if (el) rowRefs[idx] = el;
                  }
                "
                :item="item"
                @update="updateItem(idx, $event)"
                @remove="removeItem(idx)"
                @title-enter="onRowTitleEnter(idx)"
                @open-detail="openDetail(idx)"
              />
            </template>
          </draggable>
        </div>
        <div
          v-else
          class="text-sm text-n-slate-10 py-4 text-center border border-dashed border-n-weak rounded-xl"
        >
          {{ t('KANBAN.TASK_GROUPS.ITEMS_EMPTY') }}
        </div>
      </div>
    </div>

    <!-- Painel lateral direito (igual ao TaskModal) -->
    <Teleport to="body">
      <div
        v-if="detailItem"
        class="fixed inset-0 z-50 flex justify-end"
        @click.self="closeDetail"
      >
        <div
          class="w-[540px] h-full bg-n-background shadow-xl flex flex-col border-l border-n-weak overflow-y-auto"
        >
          <TaskGroupItemDetail
            :item="detailItem"
            @update="onDetailUpdate"
            @close="closeDetail"
            @remove="onDetailRemove"
          />
        </div>
      </div>
    </Teleport>
  </div>
</template>
